// EmployerTeam.jsx
// In EmployerDashboard.jsx:
//   import EmployerTeam from './EmployerTeam'
//   {nav === 'team' && <EmployerTeam t={t} employees={employees} checkIns={checkIns} pdrCounts={pdrCounts} orgId={profile.org_id} />}

import { useState } from 'react'
import { get } from '../lib/api'

function moodEmoji(m) { return { great:'😄', good:'🙂', okay:'😐', tough:'😔' }[m] || '—' }
function moodColor(m) { return { great:'#059669', good:'#4F46E5', okay:'#D97706', tough:'#EF4444' }[m] || '#64748B' }
function scoreColor(s) { return s>=8?'#059669':s>=6?'#4F46E5':s>=4?'#D97706':'#EF4444' }
function scoreLabel(s) { return s>=8?'Outstanding':s>=6?'Good':s>=4?'Average':'Below Average' }

const EMP_COLORS = [
  'linear-gradient(135deg,#4F46E5,#6366F1)',
  'linear-gradient(135deg,#059669,#10B981)',
  'linear-gradient(135deg,#D97706,#F59E0B)',
  'linear-gradient(135deg,#7C3AED,#8B5CF6)',
  'linear-gradient(135deg,#DB2777,#EC4899)',
  'linear-gradient(135deg,#0EA5E9,#38BDF8)',
  'linear-gradient(135deg,#EF4444,#F87171)',
  'linear-gradient(135deg,#F97316,#FB923C)',
]
function empColor(i) { return EMP_COLORS[i % EMP_COLORS.length] }

export default function EmployerTeam({ t, employees, checkIns, pdrCounts, orgId, allMeetings }) {
  const [search, setSearch]           = useState('')
  const [selectedEmp, setSelectedEmp] = useState(null)
  const [empPdrs, setEmpPdrs]         = useState([])
  const [empReviews, setEmpReviews]   = useState([])
  const [loadingDetail, setLoadingDetail] = useState(false)

  async function openEmployee(emp) {
    setSelectedEmp(emp)
    setLoadingDetail(true)
    try {
      const [pdrsRes, reviewsRes] = await Promise.all([
        get(`/pdrs?employee_id=${emp.id}`),
        get(`/reviews?employee_id=${emp.id}`),
      ])
      setEmpPdrs(pdrsRes.pdrs || [])
      setEmpReviews(reviewsRes.reviews || [])
    } catch (e) {
      console.error(e.message)
    }
    setLoadingDetail(false)
  }

  const filtered = employees.filter(e =>
    !search || e.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    e.position?.toLowerCase().includes(search.toLowerCase()) ||
    e.department?.toLowerCase().includes(search.toLowerCase())
  )

  const today = new Date().toISOString().split('T')[0]

  if (selectedEmp) {
    const empCheckIns = checkIns.filter(c => c.employee_id === selectedEmp.id)
    const todayRec    = empCheckIns.find(c => c.date === today)
    const last7moods  = empCheckIns.slice(0, 7).reverse()
    const recentBlockers = empCheckIns.filter(c => c.blockers).slice(0, 5)
    const activePdr   = empPdrs.find(p => p.status === 'active')
    const latestReview = empReviews[0]
    const avgScore = empReviews.length
      ? (empReviews.reduce((s,r) => s + (r.manager_score||r.final_score||0), 0) / empReviews.length).toFixed(1)
      : null

    return (
      <div>
        <button onClick={() => { setSelectedEmp(null); setEmpPdrs([]); setEmpReviews([]) }}
          style={{ fontSize:13, color:t.textMuted, background:'none', border:'none', cursor:'pointer', marginBottom:'1.25rem', fontFamily:'Inter,system-ui' }}>
          ← Back to team
        </button>

        {/* Employee header */}
        <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:16, padding:'1.75rem', marginBottom:16, boxShadow:t.shadow }}>
          <div style={{ display:'flex', alignItems:'flex-start', gap:20 }}>
            <div style={{ width:72, height:72, borderRadius:'50%', background:'linear-gradient(135deg,#4F46E5,#6366F1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:28, fontWeight:800, color:'#fff', flexShrink:0 }}>
              {selectedEmp.full_name?.charAt(0)}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:22, fontWeight:900, color:t.text, letterSpacing:'-0.5px', marginBottom:4 }}>{selectedEmp.full_name}</div>
              <div style={{ fontSize:14, color:'#4F46E5', fontWeight:600, marginBottom:4 }}>{selectedEmp.position || '—'} {selectedEmp.department && `· ${selectedEmp.department}`}</div>
              <div style={{ fontSize:13, color:t.textMuted }}>{selectedEmp.email}</div>
              <div style={{ display:'flex', gap:8, marginTop:10, flexWrap:'wrap' }}>
                {selectedEmp.joined_date && <span style={{ fontSize:11, fontWeight:600, background:'#F1F5F9', color:'#64748B', padding:'3px 10px', borderRadius:99 }}>📅 Joined {selectedEmp.joined_date}</span>}
                {selectedEmp.years_experience && <span style={{ fontSize:11, fontWeight:600, background:'#F1F5F9', color:'#64748B', padding:'3px 10px', borderRadius:99 }}>{selectedEmp.years_experience} yrs experience</span>}
                {avgScore && <span style={{ fontSize:11, fontWeight:700, background:`${scoreColor(avgScore)}14`, color:scoreColor(avgScore), padding:'3px 10px', borderRadius:99 }}>⭐ Avg score: {avgScore}/10</span>}
              </div>
            </div>
            {/* Today's mood */}
            {todayRec?.checkin_mood && (
              <div style={{ textAlign:'center', flexShrink:0 }}>
                <div style={{ fontSize:11, color:t.textMuted, marginBottom:4 }}>Today's mood</div>
                <div style={{ fontSize:36 }}>{moodEmoji(todayRec.checkin_mood)}</div>
                <div style={{ fontSize:12, fontWeight:700, color:moodColor(todayRec.checkin_mood), textTransform:'capitalize' }}>{todayRec.checkin_mood}</div>
              </div>
            )}
          </div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>

          {/* Mood trend */}
          <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.25rem', boxShadow:t.shadow }}>
            <div style={{ fontSize:14, fontWeight:700, color:t.text, marginBottom:'1rem' }}>😊 Mood Trend (last 7 days)</div>
            {last7moods.length === 0 ? (
              <p style={{ fontSize:13, color:t.textMuted }}>No check-in data yet.</p>
            ) : (
              <div style={{ display:'flex', gap:8, alignItems:'flex-end' }}>
                {last7moods.map((c, i) => {
                  const mood = c.checkin_mood || c.mood
                  return (
                    <div key={i} style={{ flex:1, textAlign:'center' }}>
                      <div style={{ fontSize:22, marginBottom:4 }}>{moodEmoji(mood)}</div>
                      <div style={{ fontSize:9, color:t.textMuted, lineHeight:1.2 }}>
                        {new Date(c.date+'T00:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'})}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Recent blockers */}
          <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.25rem', boxShadow:t.shadow }}>
            <div style={{ fontSize:14, fontWeight:700, color:t.text, marginBottom:'1rem' }}>⚠️ Recent Hurdles / Blockers</div>
            {recentBlockers.length === 0 ? (
              <p style={{ fontSize:13, color:'#059669' }}>✓ No blockers reported recently</p>
            ) : recentBlockers.map(c => (
              <div key={c.id} style={{ padding:'8px 0', borderBottom:`1px solid ${t.border}` }}>
                <div style={{ fontSize:11, color:t.textMuted, marginBottom:2 }}>{c.date}</div>
                <p style={{ fontSize:13, color:'#DC2626', margin:0, lineHeight:1.5 }}>{c.blockers}</p>
              </div>
            ))}
          </div>
        </div>

        {loadingDetail ? <p style={{ color:t.textMuted }}>Loading details…</p> : (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>

            {/* Active PDR */}
            <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.25rem', boxShadow:t.shadow }}>
              <div style={{ fontSize:14, fontWeight:700, color:t.text, marginBottom:'1rem' }}>📋 Active PDR</div>
              {!activePdr ? (
                <p style={{ fontSize:13, color:t.textMuted }}>No active PDR cycle.</p>
              ) : (
                <>
                  <div style={{ fontSize:14, fontWeight:700, color:t.text, marginBottom:4 }}>{activePdr.title}</div>
                  <div style={{ fontSize:12, color:t.textMuted, marginBottom:12 }}>
                    {activePdr.start_date} → {activePdr.end_date}
                  </div>
                  {(activePdr.goals || []).map((g, i) => {
                    const GCOLS = ['#4F46E5','#059669','#D97706','#7C3AED','#DB2777']
                    const col = GCOLS[i % GCOLS.length]
                    return (
                      <div key={g.id} style={{ marginBottom:10 }}>
                        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                          <span style={{ fontSize:12, fontWeight:600, color:t.text }}>{g.title}</span>
                          <div style={{ display:'flex', gap:6 }}>
                            <span style={{ fontSize:11, color:col, fontWeight:700 }}>{g.weightage}%</span>
                            <span style={{ fontSize:11, color:t.textMuted }}>{g.progress}%</span>
                          </div>
                        </div>
                        <div style={{ background:t.border, borderRadius:99, height:6 }}>
                          <div style={{ width:`${g.progress}%`, height:'100%', background:col, borderRadius:99 }} />
                        </div>
                      </div>
                    )
                  })}
                </>
              )}
            </div>

            {/* Reviews + Rating */}
            <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.25rem', boxShadow:t.shadow }}>
              <div style={{ fontSize:14, fontWeight:700, color:t.text, marginBottom:'1rem' }}>⭐ Performance Reviews</div>
              {empReviews.length === 0 ? (
                <p style={{ fontSize:13, color:t.textMuted }}>No reviews yet.</p>
              ) : (
                <>
                  {avgScore && (
                    <div style={{ background:`${scoreColor(avgScore)}10`, border:`1px solid ${scoreColor(avgScore)}30`, borderRadius:10, padding:'12px', marginBottom:12, textAlign:'center' }}>
                      <div style={{ fontSize:10, color:t.textMuted, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:4 }}>Overall Rating</div>
                      <div style={{ fontSize:36, fontWeight:900, color:scoreColor(avgScore), lineHeight:1 }}>{avgScore}<span style={{ fontSize:16, fontWeight:400 }}>/10</span></div>
                      <div style={{ fontSize:12, fontWeight:700, color:scoreColor(avgScore) }}>{scoreLabel(parseFloat(avgScore))}</div>
                    </div>
                  )}
                  {empReviews.slice(0,3).map(r => (
                    <div key={r.id} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:`1px solid ${t.border}` }}>
                      <span style={{ fontSize:13, color:t.text }}>Review {r.year || r.review_period}</span>
                      {(r.manager_score||r.final_score) && (
                        <span style={{ fontSize:14, fontWeight:800, color:scoreColor(r.manager_score||r.final_score) }}>
                          {r.manager_score||r.final_score}/10
                        </span>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* Meetings */}
            {(() => {
              const empMeetings = (allMeetings || []).filter(m =>
                m.employee_id === selectedEmp.id ||
                (m.attendee_ids || []).includes(selectedEmp.id)
              )
              return (
                <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.25rem', boxShadow:t.shadow }}>
                  <div style={{ fontSize:14, fontWeight:700, color:t.text, marginBottom:'1rem' }}>🗓 Meetings ({empMeetings.length})</div>
                  {empMeetings.length === 0 ? (
                    <p style={{ fontSize:13, color:t.textMuted }}>No meetings scheduled.</p>
                  ) : empMeetings.slice(0,5).map(m => {
                    const actionItems = m.structured_action_items || []
                    const done = actionItems.filter(i=>i.completed).length
                    return (
                      <div key={m.id} style={{ padding:'8px 0', borderBottom:`1px solid ${t.border}` }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:3 }}>
                          <span style={{ fontSize:13, fontWeight:600, color:t.text }}>{m.title || '1:1 Meeting'}</span>
                          <span style={{ fontSize:11, fontWeight:700, padding:'2px 7px', borderRadius:99,
                            background:m.status==='completed'?'#ECFDF5':m.status==='in_progress'?'#FFFBEB':'#EFF6FF',
                            color:m.status==='completed'?'#059669':m.status==='in_progress'?'#D97706':'#4F46E5' }}>
                            {m.status==='completed'?'✅ Done':m.status==='in_progress'?'🗣️ In Progress':'📅 Scheduled'}
                          </span>
                        </div>
                        <div style={{ fontSize:11, color:t.textMuted }}>
                          {m.meeting_date}{m.meeting_time && ` · ${m.meeting_time}`}
                          {actionItems.length > 0 && ` · ${done}/${actionItems.length} actions done`}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )
            })()}

            {/* Job description */}
            {selectedEmp.job_description && (
              <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.25rem', boxShadow:t.shadow, gridColumn:'1 / -1' }}>
                <div style={{ fontSize:14, fontWeight:700, color:t.text, marginBottom:'0.75rem' }}>📄 Job Description</div>
                <p style={{ fontSize:13, color:t.text, margin:0, lineHeight:1.8 }}>{selectedEmp.job_description}</p>
              </div>
            )}

            {/* Skills */}
            {selectedEmp.skills && (
              <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.25rem', boxShadow:t.shadow }}>
                <div style={{ fontSize:14, fontWeight:700, color:t.text, marginBottom:'0.75rem' }}>🛠 Skills</div>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {selectedEmp.skills.split(',').map(s => s.trim()).filter(Boolean).map(s => (
                    <span key={s} style={{ fontSize:12, fontWeight:600, background:'#4F46E514', color:'#4F46E5', padding:'4px 10px', borderRadius:99 }}>{s}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Today's plan */}
            {todayRec?.plan_today && (
              <div style={{ background:'#EEF2FF', border:'1px solid #C7D2FE', borderRadius:14, padding:'1.25rem', boxShadow:t.shadow }}>
                <div style={{ fontSize:14, fontWeight:700, color:'#4F46E5', marginBottom:'0.75rem' }}>📋 Today's Plan</div>
                <p style={{ fontSize:13, color:t.text, margin:0, lineHeight:1.7 }}>{todayRec.plan_today}</p>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1.75rem' }}>
        <div>
          <h1 style={{ fontSize:24, fontWeight:800, color:t.text, margin:'0 0 4px', letterSpacing:'-0.5px' }}>Team</h1>
          <p style={{ fontSize:14, color:t.textMuted, margin:0 }}>{employees.length} employees</p>
        </div>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search name, role, department…"
          style={{ padding:'9px 14px', background:t.card, border:`1px solid ${t.border}`, borderRadius:8, fontSize:13, color:t.text, outline:'none', fontFamily:'Inter,system-ui', width:240 }} />
      </div>

      {filtered.length === 0 ? (
        <p style={{ color:t.textMuted }}>No employees found.</p>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:14 }}>
          {filtered.map((emp, empIdx) => {
            const today = new Date().toISOString().split('T')[0]
            const todayIn = checkIns.some(c => c.employee_id === emp.id && c.date === today)
            const pdr = pdrCounts[emp.id] || { total:0 }
            const recentMoods = checkIns.filter(c => c.employee_id === emp.id).slice(0, 5)
            const hasBlocker  = checkIns.find(c => c.employee_id === emp.id && c.blockers && c.date === today)
            const col = empColor(empIdx)

            return (
              <div key={emp.id} onClick={() => openEmployee(emp)}
                style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.25rem', boxShadow:t.shadow, cursor:'pointer', position:'relative' }}>

                {hasBlocker && (
                  <div style={{ position:'absolute', top:12, right:12, width:10, height:10, borderRadius:'50%', background:'#EF4444' }} title="Has blocker today" />
                )}

                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
                  <div style={{ width:46, height:46, borderRadius:'50%', background:col, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, fontWeight:700, color:'#fff', flexShrink:0 }}>
                    {emp.full_name?.charAt(0)}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:15, fontWeight:700, color:t.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{emp.full_name}</div>
                    <div style={{ fontSize:12, color:'#4F46E5', fontWeight:600 }}>{emp.position || '—'}</div>
                    {emp.department && <div style={{ fontSize:11, color:t.textMuted }}>{emp.department}</div>}
                  </div>
                </div>

                {/* Status row */}
                <div style={{ display:'flex', gap:6, marginBottom:10, flexWrap:'wrap' }}>
                  <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:99, background:todayIn?'#ECFDF5':'#FEF2F2', color:todayIn?'#059669':'#EF4444' }}>
                    {todayIn ? '✓ In today' : '✗ Absent today'}
                  </span>
                  {pdr.total > 0 && (
                    <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:99, background:'#EEF2FF', color:'#4F46E5' }}>
                      {pdr.total} PDR{pdr.total>1?'s':''}
                    </span>
                  )}
                </div>

                {/* Mini mood strip */}
                {recentMoods.length > 0 && (
                  <div style={{ display:'flex', gap:4, marginBottom:10 }}>
                    <span style={{ fontSize:11, color:t.textMuted, alignSelf:'center' }}>Mood:</span>
                    {recentMoods.map((c,i) => (
                      <span key={i} style={{ fontSize:14 }}>{moodEmoji(c.checkin_mood || c.mood)}</span>
                    ))}
                  </div>
                )}

                <div style={{ fontSize:12, color:'#4F46E5', fontWeight:600 }}>View full profile →</div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
